import { TypedDocumentNode, gql } from '@apollo/client';
import classnames from 'classnames';
import { FormattedMessage, MessageDescriptor } from 'react-intl';
import { Link } from 'react-router-dom';
import styled from 'styled-components';

import { Horizontal, Vertical } from 'atoms/layout/flex';
import { Skeleton } from 'atoms/loader/Skeleton';
import { Caption, Text16 } from 'atoms/typography';
import Avatar, { PlaceHolderAvatar } from 'components/user/Avatar';
import UserName from 'components/user/UserName';
import { ANY_SPORT_MY_CLUB } from 'constants/routes';
import { useSportContext } from 'contexts/sport';

import { User_user } from './__generated__/index.graphql';

type Props = {
  user?: User_user | null;
  disabled?: boolean;
  context?: MessageDescriptor;
  reverse?: boolean;
  loading?: boolean;
};

const UserLayout = styled(Horizontal)`
  color: var(--c-neutral-1000);
  align-items: center;
  overflow: hidden;
  &.disabled {
    color: var(--c-neutral-600);
  }
  &.context {
    flex-direction: row-reverse;
  }
  &.reverse {
    flex-direction: row-reverse;
  }
`;

const UserContainer = styled(Vertical).attrs({ gap: 0 })`
  max-width: 100%;
  overflow: hidden;
`;

const Wrapper = styled(Text16)`
  overflow: hidden;
  margin-right: var(--unit);
  white-space: nowrap;
  text-overflow: ellipsis;
`;

type SorareUserProps = Props & {
  user: User_user;
};

const SorareSuspendedUser = ({
  user,
  disabled,
  reverse,
  context,
}: SorareUserProps) => {
  return (
    <UserLayout className={classnames({ disabled, reverse })}>
      <UserContainer>
        {context && (
          <Caption color="var(--c-neutral-600)">
            <FormattedMessage {...context} />
          </Caption>
        )}
        <Wrapper>
          <UserName user={user} />
        </Wrapper>
      </UserContainer>
      <Avatar user={user} />
    </UserLayout>
  );
};

const SorareRegularUser = ({
  user,
  disabled,
  reverse,
  context,
}: SorareUserProps) => {
  const { generateSportContextPath } = useSportContext();
  return (
    <UserLayout
      as={Link}
      className={classnames({ disabled, reverse, context })}
      to={generateSportContextPath(ANY_SPORT_MY_CLUB, {
        params: { slug: user.slug },
      })}
    >
      <UserContainer>
        {context && (
          <Caption color="var(--c-neutral-600)">
            <FormattedMessage {...context} />
          </Caption>
        )}
        <Wrapper>
          <UserName user={user} />
        </Wrapper>
      </UserContainer>
      <Avatar user={user} />
    </UserLayout>
  );
};

const SorareUser = ({ user, ...rest }: SorareUserProps) => {
  if (user.suspended) {
    return <SorareSuspendedUser user={user} {...rest} />;
  }
  return <SorareRegularUser user={user} {...rest} />;
};

export const User = ({ user, loading, ...rest }: Props) => {
  if (!user) {
    if (!loading) return null;
    return (
      <Horizontal>
        <Skeleton loading>
          <PlaceHolderAvatar />
        </Skeleton>
        <Skeleton loading />
      </Horizontal>
    );
  }

  return <SorareUser user={user} {...rest} />;
};

User.fragments = {
  user: gql`
    fragment User_user on User {
      slug
      ...Avatar_publicUserInfoInterface
      ...UserName_publicUserInfoInterface
    }
    ${Avatar.fragments.publicUserInfoInterface}
    ${UserName.fragments.user}
  ` as TypedDocumentNode<User_user>,
};

export default User;
